/**
 * apiError — turn an axios/FastAPI error into something a land analyst can act on.
 *
 * Axios's own `err.message` for a rejected request is "Request failed with
 * status code 422", which tells the user nothing: not which field, not what
 * about it. The actionable part is in the response body, and FastAPI puts it
 * in one of three shapes:
 *
 *   422 (Pydantic validation)  { detail: [{ loc: ["body","effective_date"],
 *                                           msg: "Field required",
 *                                           type: "missing" }, ...] }
 *   4xx/5xx raised by a route  { detail: "deal_key already exists" }
 *   400 (broker_deals field    { detail: { field: "county",
 *        validators)                       message: "Invalid county: 'UNKNOWN'" } }
 *
 * Rendering the array shape directly into an Alert throws "Objects are not
 * valid as a React child", which is why half the app had its own private
 * `errText` helper. This module is the one implementation: it resolves each
 * `loc` to the field's human label from the table's UI metadata, so the user
 * reads "Lessor/Grantor is required" rather than "body.lessor_grantor:
 * Field required".
 *
 * Pass the same `metadata` array the form was built from. Without it the
 * output degrades to the raw field name — still readable, just less friendly.
 */

// Field-name segments to drop from a Pydantic `loc` path. FastAPI prefixes
// every body-validation loc with the request part it came from.
const LOC_PREFIXES = new Set(["body", "query", "path", "header", "cookie"]);

/**
 * Title-case a snake_case column name as a last-resort label:
 * "lessor_grantor" -> "Lessor Grantor".
 */
const humanizeField = (field) =>
  String(field)
    .split("_")
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");

/**
 * Resolve a column name to its curated UI label, falling back to a
 * title-cased version of the column name.
 *
 * @param {Array<{field: string, label?: string}>} metadata
 * @param {string} field
 * @returns {string}
 */
export const fieldLabel = (metadata, field) => {
  const row = (metadata || []).find((m) => m?.field === field);
  return row?.label || humanizeField(field);
};

/**
 * The field names a 422 complained about, in order. Useful for scrolling to
 * or highlighting the offending inputs.
 *
 * @param {unknown} err — an axios error
 * @returns {string[]} column names (may be empty)
 */
export const invalidFields = (err) => {
  const detail = err?.response?.data?.detail;
  // Structured 400 (`{field, message}`) names exactly one field.
  if (detail && !Array.isArray(detail) && typeof detail === "object") {
    return typeof detail.field === "string" && detail.field ? [detail.field] : [];
  }
  if (!Array.isArray(detail)) return [];
  return detail
    .map((d) => (d?.loc || []).filter((p) => !LOC_PREFIXES.has(p)))
    // The last non-prefix segment is the leaf field; earlier segments are
    // nesting (e.g. ["tracts", 0, "county"]).
    .map((path) => path.filter((p) => typeof p === "string").pop())
    .filter(Boolean);
};

/**
 * Render one Pydantic validation entry as a sentence.
 */
const detailEntryText = (entry, metadata) => {
  if (typeof entry === "string") return entry;
  const path = (entry?.loc || []).filter(
    (p) => !LOC_PREFIXES.has(p) && typeof p === "string"
  );
  const field = path[path.length - 1];
  const msg = entry?.msg || "is invalid";

  if (!field) return msg;

  const label = fieldLabel(metadata, field);
  // "Field required" reads as a fragment when prefixed with a label, so
  // rewrite the two most common Pydantic messages into whole sentences.
  if (entry?.type === "missing" || /^field required$/i.test(msg)) {
    return `${label} is required`;
  }
  if (/^input should be/i.test(msg)) {
    return `${label}: ${msg.replace(/^Input should be/i, "must be")}`;
  }
  return `${label}: ${msg}`;
};

/**
 * Render de-hub's structured 400 body as a sentence.
 *
 * The broker_deals validators (`_validate_pick_lists`,
 * `_tract_validation_error_response`) reject a save with a single-field object
 * rather than Pydantic's array:
 *
 *   400 { detail: { field: "county", message: "Invalid county: 'UNKNOWN'" } }
 *
 * That shape used to fall past every branch below and land on axios's
 * `err.message`, so the user got "Request failed with status code 400" for a
 * rejection the server had already explained field-by-field.
 *
 * The message already embeds the raw column name ("Invalid county:"), so
 * prefixing the label would read "County: Invalid county: ..." — swap the
 * column name for the label in place instead.
 */
const objectDetailText = (detail, metadata) => {
  const field = typeof detail?.field === "string" ? detail.field : null;
  const msg = [detail?.message, detail?.msg, detail?.detail, detail?.error].find(
    (m) => typeof m === "string" && m.trim()
  );
  if (!msg) {
    // Never return the object itself — React refuses to render it and the
    // caller has no way to know that until it throws.
    try {
      return JSON.stringify(detail);
    } catch {
      return null;
    }
  }
  if (!field) return msg;

  const label = fieldLabel(metadata, field);
  const inlineField = new RegExp(`^(invalid\\s+)${field}\\b`, "i");
  if (inlineField.test(msg)) return msg.replace(inlineField, `$1${label}`);
  if (msg.toLowerCase().startsWith(`${label.toLowerCase()} `)) return msg;
  return `${label}: ${msg}`;
};

// Status codes whose generic axios message is worse than useless, mapped to
// what actually went wrong. Only used when the body carries no `detail`.
const STATUS_TEXT = {
  400: "The server rejected that change. Check the values and try again.",
  401: "Your session has expired — refresh the page and sign in again.",
  403: "You don't have permission to do that.",
  404: "That record no longer exists — it may have been deleted.",
  409: "That change conflicts with an existing record.",
  413: "That file is too large to upload.",
  502: "The server is unreachable. Try again in a moment.",
  503: "The server is unreachable. Try again in a moment.",
  504: "The server took too long to respond. Try again in a moment.",
};

/**
 * Normalize any axios/FastAPI error into a single human-readable string.
 *
 * @param {unknown} err — the caught error (axios error, Error, or anything)
 * @param {object} [opts]
 * @param {Array<{field: string, label?: string}>} [opts.metadata]
 *   UI metadata for the entity being saved — supplies field labels.
 * @param {string} [opts.fallback] — used when nothing else is available.
 * @returns {string}
 */
export const apiErrorText = (err, opts = {}) => {
  const { metadata, fallback = "Something went wrong. Please try again." } =
    opts;

  const status = err?.response?.status;
  const data = err?.response?.data;
  const detail = data?.detail;

  if (Array.isArray(detail) && detail.length) {
    const parts = detail
      .map((d) => detailEntryText(d, metadata))
      .filter(Boolean);
    if (parts.length) {
      // Validation errors are a checklist, not prose — join with "; " so a
      // multi-field failure stays scannable in an Alert.
      return parts.join("; ");
    }
  }
  if (typeof detail === "string" && detail.trim()) return detail;
  // Single-field validator rejection — `{field, message}` (see above).
  if (detail && typeof detail === "object" && !Array.isArray(detail)) {
    const text = objectDetailText(detail, metadata);
    if (text) return text;
  }
  // Some de-hub routes return `{message}` instead of `{detail}`.
  if (typeof data?.message === "string" && data.message.trim()) {
    return data.message;
  }

  if (STATUS_TEXT[status]) return STATUS_TEXT[status];

  // No response at all — the request never reached the proxy.
  if (err?.request && !err?.response) {
    return "Couldn't reach the server. Check your connection and try again.";
  }

  return err?.message || fallback;
};

/**
 * Same as `apiErrorText`, prefixed with what the app was trying to do, e.g.
 * `saveErrorText(err, "save the lease/deed", {metadata})` ->
 * "Couldn't save the lease/deed. Lessor/Grantor is required".
 *
 * Keeping the action in the message matters because most tabs render load and
 * save failures through the same Alert — without it, a failed save reads as a
 * failed load and the user reloads the page instead of fixing the form.
 *
 * @param {unknown} err
 * @param {string} action — an infinitive phrase, no leading "to".
 * @param {object} [opts] — as `apiErrorText`.
 * @returns {string}
 */
export const saveErrorText = (err, action, opts = {}) =>
  `Couldn't ${action}. ${apiErrorText(err, opts)}`;

export default apiErrorText;
